import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import * as THREE from 'three';
import { Product } from '../../PIM/models/product.model';
import { ProductOrientation } from '../../PIM/models/product-orientation.interface';
import { takeUntil, tap } from 'rxjs/operators';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { TakeUntilDestroy } from '../../core/decorators/take-until-destroy';
import { Observable } from 'rxjs';
import { PimService } from '../../PIM/services/pim.service';

@Component({
  selector: 'app-product-visualization',
  templateUrl: './product-visualization.component.html',
  styleUrls: ['./product-visualization.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
@TakeUntilDestroy
export class ProductVisualizationComponent implements OnDestroy, OnInit {
  private readonly CONST = {
    LABEL_BASE_SCALE: 0.06,
    DEFAULT_CAMERA_DISTANCE_MULTIPLIER: 1.2,
    LABEL_DELTA_FACTOR: 0.02,
    SPRITE_DEFAULT_FONT_SIZE: 64,
    SPRITE_DEFAULT_PADDING: 14,
    SPRITE_DEFAULT_BACKGROUND: 'rgba(255,255,255,0.9)',
    LABEL_BACKGROUND: 'rgba(255,255,255,0.95)',
    LABEL_TEXT_COLOR_PRODUCT: '#111',
    LABEL_TEXT_COLOR_LOCATION: '#222',
    CAMERA: { FOV: 75, NEAR: 0.1, FAR: 5000 },
    AMBIENT_INTENSITY: 0.6,
    DIRECTIONAL_INTENSITY: 1.0,
    LIGHT_DISTANCE_MULTIPLIER: 1.5,
    LIGHT_HEIGHT_MULTIPLIER: 1.2,
    SHADOW_MAP_SIZE: 2048,
    SHADOW_FAR_MULTIPLIER: 4,
    OUTER_OPACITY: 0,
    LINE_WIDTH: 2,
    DEFAULT_PIXEL_RATIO: 1,
    COLORS: {
      EDGE: 0x000000,
      OUTER: 0x888888,
      INNER: ['#ADFF2F', '#ADFF2F', '#32CD32', '#32CD32', '#008c00', '#008c00'],
    },
  } as const;

  @Input() productId: number;

  @Input() locationSize: ProductOrientation;

  @ViewChild('rendererCanvas', { static: true })
  public rendererCanvas: ElementRef<HTMLCanvasElement>;

  componentDestroy: () => Observable<void>;

  product: Product;

  productWidth: number;

  productHeight: number;

  productLength: number;

  private canvas: HTMLCanvasElement;

  private renderer: THREE.WebGLRenderer;

  private camera: THREE.PerspectiveCamera;

  private scene: THREE.Scene;

  private ambientLight: THREE.AmbientLight;

  private dirLight: THREE.DirectionalLight;

  private cube: THREE.Mesh;

  private cubeEdges: THREE.LineSegments;

  private outerBox: THREE.Mesh;

  private outerEdges: THREE.LineSegments;

  private frameId: number = null;

  private maxSceneDimensions = 1; // used to scale labels

  private onDOMContentLoaded = () => {};

  private onWindowResize = () => {};

  constructor(private ngZone: NgZone, private pimService: PimService) {}

  public ngOnDestroy(): void {
    if (this.frameId != null) cancelAnimationFrame(this.frameId);

    window.removeEventListener('resize', this.onWindowResize);
    window.removeEventListener('DOMContentLoaded', this.onDOMContentLoaded);

    if (this.renderer != null) {
      this.renderer.dispose();
      this.renderer = null;
      this.canvas = null;
    }
  }

  ngOnInit() {
    this.canvas = this.rendererCanvas.nativeElement;
    this.pimService
      .getProduct(this.productId)
      .pipe(
        tap((product: Product) => {
          this.product = product;
          this.setProductDimensions();
          this.generateModel();
        }),
        takeUntil(this.componentDestroy()),
      )
      .subscribe();
  }

  private setProductDimensions() {
    if (this.product.package !== null) {
      const { width, height, length } = this.product.package.dimensions;

      if (width > 0 && height > 0 && length > 0) {
        this.productWidth = width;
        this.productHeight = height;
        this.productLength = length;

        return;
      }
    }

    this.productWidth = this.product.dimensions.width;
    this.productHeight = this.product.dimensions.height;
    this.productLength = this.product.dimensions.length;
  }

  private createTextSprite(
    text: string,
    options?: { color?: string; backgroundColor?: string; fontSize?: number; padding?: number },
  ) {
    const color = options?.color ?? '#000';
    const backgroundColor = options?.backgroundColor ?? this.CONST.SPRITE_DEFAULT_BACKGROUND;
    const fontSize = options?.fontSize ?? this.CONST.SPRITE_DEFAULT_FONT_SIZE;
    const padding = options?.padding ?? this.CONST.SPRITE_DEFAULT_PADDING;

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    context.font = `bold ${fontSize}px Arial`;
    const textWidth = context.measureText(text).width;
    canvas.width = textWidth + padding * 2;
    canvas.height = fontSize + padding * 2;

    context.fillStyle = backgroundColor;
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.font = `bold ${fontSize}px Arial`;
    context.fillStyle = color;
    context.textBaseline = 'top';
    context.fillText(text, padding, padding);

    const texture = new THREE.CanvasTexture(canvas);
    const material = new THREE.SpriteMaterial({
      map: texture,
      transparent: true,
      depthTest: false,
      depthWrite: false,
    });
    const sprite = new THREE.Sprite(material);

    const baseScale = this.CONST.LABEL_BASE_SCALE * this.maxSceneDimensions;
    const aspect = canvas.width / canvas.height;
    sprite.scale.set(baseScale * aspect, baseScale, 1);
    sprite.renderOrder = 9999;
    return sprite;
  }

  // Shows front (+Z), right (+X), top (+Y); attached to the mesh so labels follow cube rotation.
  private addFaceLabels(box: THREE.Mesh, size: { w: number; h: number; d: number }, color: string, isProduct: boolean) {
    const half = { x: size.w / 2, y: size.h / 2, z: size.d / 2 };
    const delta = this.CONST.LABEL_DELTA_FACTOR * Math.max(size.w, size.h, size.d);

    let labelsText: { labelA: string; labelB: string; labelC: string };
    if (isProduct) {
      labelsText = this.getLabels(size);
    } else {
      labelsText = {
        labelA: `front ${size.w.toFixed(0)}×${size.h.toFixed(0)} mm`,
        labelB: `right ${size.d.toFixed(0)}×${size.h.toFixed(0)} mm`,
        labelC: `top ${size.w.toFixed(0)}×${size.d.toFixed(0)} mm`,
      };
    }

    const labels: { text: string; pos: THREE.Vector3 }[] = [
      { text: labelsText.labelA, pos: new THREE.Vector3(0, 0, half.z + delta) },
      { text: labelsText.labelB, pos: new THREE.Vector3(half.x + delta, 0, 0) },
      { text: labelsText.labelC, pos: new THREE.Vector3(0, half.y + delta, 0) },
    ];
    labels.forEach(l => {
      const sprite = this.createTextSprite(l.text, { color, backgroundColor: this.CONST.LABEL_BACKGROUND });
      sprite.position.copy(l.pos);
      box.add(sprite);
    });
  }

  private getLabels(size: { w: number; h: number; d: number }) {
    const width = this.productWidth;
    const height = this.productHeight;
    const length = this.productLength;

    const fmt = (name: string, a: number, b: number) => `${name} ${a.toFixed(0)}×${b.toFixed(0)} mm`;

    // Front face (+Z): uses size.w (horizontal) × size.h (vertical)
    const frontDims = [size.w, size.h].sort((a, b) => a - b);
    // Right face (+X): uses size.d (horizontal) × size.h (vertical)
    const rightDims = [size.d, size.h].sort((a, b) => a - b);
    // Top face (+Y): uses size.w (horizontal) × size.d (depth)
    const topDims = [size.w, size.d].sort((a, b) => a - b);

    // Original product face dimensions (sorted for comparison)
    const originalFront = [width, height].sort((a, b) => a - b); // width × height
    const originalRight = [length, height].sort((a, b) => a - b); // length × height
    const originalTop = [width, length].sort((a, b) => a - b); // width × length

    const dimsMatch = (a: number[], b: number[]) => a[0] === b[0] && a[1] === b[1];

    // Identify labelA (front face +Z)
    let labelA: string | null = null;
    if (dimsMatch(frontDims, originalFront)) {
      labelA = fmt('front', width, height);
    } else if (dimsMatch(frontDims, originalRight)) {
      labelA = fmt('right', length, height);
    } else if (dimsMatch(frontDims, originalTop)) {
      labelA = fmt('top', width, length);
    }

    // Identify labelB (right face +X)
    let labelB: string | null = null;
    if (dimsMatch(rightDims, originalFront)) {
      labelB = fmt('front', width, height);
    } else if (dimsMatch(rightDims, originalRight)) {
      labelB = fmt('right', length, height);
    } else if (dimsMatch(rightDims, originalTop)) {
      labelB = fmt('top', width, length);
    }

    // Identify labelC (top face +Y)
    let labelC: string | null = null;
    if (dimsMatch(topDims, originalFront)) {
      labelC = fmt('front', width, height);
    } else if (dimsMatch(topDims, originalRight)) {
      labelC = fmt('right', length, height);
    } else if (dimsMatch(topDims, originalTop)) {
      labelC = fmt('top', width, length);
    }

    return { labelA, labelB, labelC };
  }

  private setRendererSizeToCanvas() {
    const width = this.canvas.clientWidth || window.innerWidth;
    const height = this.canvas.clientHeight || window.innerHeight;
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height, false);
  }

  public generateModel() {
    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      alpha: true,
      antialias: true,
    });
    this.renderer.setPixelRatio(window.devicePixelRatio || this.CONST.DEFAULT_PIXEL_RATIO);
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    // Scene & camera
    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(this.CONST.CAMERA.FOV, 1, this.CONST.CAMERA.NEAR, this.CONST.CAMERA.FAR);
    this.scene.add(this.camera);

    // Lights
    this.ambientLight = new THREE.AmbientLight(0xffffff, this.CONST.AMBIENT_INTENSITY);
    this.scene.add(this.ambientLight);

    this.dirLight = new THREE.DirectionalLight(0xffffff, this.CONST.DIRECTIONAL_INTENSITY);
    // Position light relative to scene size for better coverage
    const lightDist = this.maxSceneDimensions * this.CONST.LIGHT_DISTANCE_MULTIPLIER;
    this.dirLight.position.set(lightDist, lightDist * this.CONST.LIGHT_HEIGHT_MULTIPLIER, lightDist);
    this.dirLight.castShadow = true;

    // Shadow map resolution
    this.dirLight.shadow.mapSize.set(this.CONST.SHADOW_MAP_SIZE, this.CONST.SHADOW_MAP_SIZE);

    // Fit shadow camera to scene bounds
    const camExtent = this.maxSceneDimensions;
    const shadowCam = this.dirLight.shadow.camera as THREE.OrthographicCamera;
    shadowCam.left = -camExtent;
    shadowCam.right = camExtent;
    shadowCam.top = camExtent;
    shadowCam.bottom = -camExtent;
    shadowCam.near = this.CONST.CAMERA.NEAR;
    shadowCam.far = this.maxSceneDimensions * this.CONST.SHADOW_FAR_MULTIPLIER;
    shadowCam.updateProjectionMatrix();

    this.scene.add(this.dirLight);

    // Orientation-adjusted product dimensions
    const innerDims = this.getProductDimensions();
    const innerWidth = innerDims.width;
    const innerHeight = innerDims.height;
    const innerDepth = innerDims.depth;

    // Outer (location) dimensions
    const outerWidth = this.locationSize.locationSize.width;
    const outerHeight = this.locationSize.locationSize.height;
    const outerDepth = this.locationSize.locationSize.length;

    // Track max dimension for label scaling
    this.maxSceneDimensions = Math.max(outerWidth, outerHeight, outerDepth) / 4;

    // Inner box (product)
    const geometry = new THREE.BoxGeometry(innerWidth, innerHeight, innerDepth);
    const materials = this.CONST.COLORS.INNER.map(color => new THREE.MeshStandardMaterial({ color }));
    this.cube = new THREE.Mesh(geometry, materials);
    this.cube.castShadow = true;
    this.cube.receiveShadow = false;

    const innerEdgesGeo = new THREE.EdgesGeometry(geometry);
    this.cubeEdges = new THREE.LineSegments(
      innerEdgesGeo,
      new THREE.LineBasicMaterial({ color: this.CONST.COLORS.EDGE, linewidth: this.CONST.LINE_WIDTH }),
    );
    this.cube.add(this.cubeEdges);

    // Outer box (location)
    const outerGeo = new THREE.BoxGeometry(outerWidth, outerHeight, outerDepth);
    const outerMat = new THREE.MeshStandardMaterial({
      color: this.CONST.COLORS.OUTER,
      transparent: true,
      opacity: this.CONST.OUTER_OPACITY,
      side: THREE.DoubleSide,
      depthWrite: false,
    });
    this.outerBox = new THREE.Mesh(outerGeo, outerMat);
    this.outerBox.castShadow = false;
    this.outerBox.receiveShadow = false;

    const outerEdgesGeo = new THREE.EdgesGeometry(outerGeo);
    this.outerEdges = new THREE.LineSegments(
      outerEdgesGeo,
      new THREE.LineBasicMaterial({ color: this.CONST.COLORS.EDGE, linewidth: this.CONST.LINE_WIDTH }),
    );
    this.outerBox.add(this.outerEdges);

    // Position inner box at left-lower-back corner inside outer box
    const offsetX = (outerWidth - innerWidth) / 2;
    const offsetY = (outerHeight - innerHeight) / 2;
    const offsetZ = (outerDepth - innerDepth) / 2;
    this.cube.position.set(-offsetX, -offsetY, -offsetZ);

    // Add face labels that follow cube rotation
    this.addFaceLabels(
      this.cube,
      { w: innerWidth, h: innerHeight, d: innerDepth },
      this.CONST.LABEL_TEXT_COLOR_PRODUCT,
      true,
    );
    this.addFaceLabels(
      this.outerBox,
      { w: outerWidth, h: outerHeight, d: outerDepth },
      this.CONST.LABEL_TEXT_COLOR_LOCATION,
      false,
    );

    const container = new THREE.Group();
    container.add(this.outerBox);
    container.add(this.cube);
    this.scene.add(container);

    const controls = new OrbitControls(this.camera, this.renderer.domElement);
    const cubeTarget = this.cube.position.clone();
    controls.target.copy(cubeTarget);
    controls.update();

    // Initial camera placement
    this.setRendererSizeToCanvas();
    const distance = this.maxSceneDimensions * this.CONST.DEFAULT_CAMERA_DISTANCE_MULTIPLIER;
    this.camera.position.set(distance, distance, distance);
    this.camera.lookAt(cubeTarget);

    // Handle window resize
    this.onWindowResize = () => {
      this.setRendererSizeToCanvas();
      this.camera.lookAt(cubeTarget);
    };
    this.animate();
  }

  public animate(): void {
    this.ngZone.runOutsideAngular(() => {
      const loop = () => {
        this.frameId = requestAnimationFrame(loop);
        this.renderer.render(this.scene, this.camera);
      };

      if (document.readyState !== 'loading') {
        loop();
      } else {
        this.onDOMContentLoaded = () => loop();
        window.addEventListener('DOMContentLoaded', this.onDOMContentLoaded);
      }
    });
  }

  private getProductDimensions() {
    const width = this.productWidth;
    const height = this.productHeight;
    const length = this.productLength;

    switch (this.locationSize.orientation) {
      case 'upright_shorter':
        return { width: Math.min(width, length), height, depth: Math.max(width, length) };
      case 'upright_longer':
        return { width: Math.max(width, length), height, depth: Math.min(width, length) };
      case 'flat_shorter':
        return { width: Math.min(width, height), height: length, depth: Math.max(width, height) };
      case 'flat_longer':
        return { width: Math.max(width, height), height: length, depth: Math.min(width, height) };
      case 'sideways_shorter':
        return { width: Math.min(height, length), height: width, depth: Math.max(height, length) };
      case 'sideways_longer':
        return { width: Math.max(height, length), height: width, depth: Math.min(height, length) };
      default:
        return { width, height, depth: length };
    }
  }
}
